import { US_STATE_NAME_TO_ABBREVIATION } from '@shared/constants/stateCapitals';
import { EventInfoData } from '@shared/utils/eventInfo';
import { PageJson } from '../routes/CardCustomizer/CardCustomizer.types';
import { TextLayerData } from '../routes/CardCustomizer/components/Layer.types';
import { getAmOrPm, getOclock, getTimeOfDay, getTwoDigitsOrdinalFromYear } from './dateFormatting';
import { mapRecord } from './mapRecord';
import { NUMERIC_ORDINALS, ORDINALS } from './ordinals';
import { toTitleCase } from './toTitleCase';
import { EventVenue } from '@shared/utils/eventPlace';

// Templates generated from the legacy theme system are already tagged.
// Each layer.name is set to one of values below.
// Rather than add a layer.pattern, we dynamically/virtually generate it.
const THEME_SYSTEM_LAYER_NAME_TO_PATTERN: Record<string, string> = {
  NAME1: '‹FIRSTNAME1›',
  NAME2: '‹FIRSTNAME2›',
  DATE: '‹WEEKDAY›, ‹MONTH› ‹D›, ‹YYYY›',
  LOCATION: '‹City›, ‹ST›'
};

function fillPattern(pattern: string, data: Record<string, string | undefined>): string {
  // Trim in case any field is empty.
  return pattern.replace(/‹(\w+)›/g, (match, key) => data[key] ?? match).trim();
}

export function fillPageWithEventInfo(page: PageJson, eventInfo: EventInfoData | undefined, venueInfo: EventVenue): string[] {
  if (!eventInfo) return [];

  const date = eventInfo.finalizedDate?.milliseconds ? new Date(eventInfo.finalizedDate.milliseconds) : undefined;
  // Using 6pm for now instead of midnight. TODO: Get time from Schedule page.
  date?.setHours(18, 0, 0, 0);
  const Firstname1 = eventInfo.ownerFirstName ?? '';
  const Firstname2 = eventInfo.fianceeFirstName ?? '';

  const Alphabetic: Record<string, string> = {
    // Names
    F1: Firstname1 ? Firstname1[0] : '',
    F2: Firstname2 ? Firstname2[0] : '',
    Firstname1,
    Firstname2,
    MiddleName1: '',
    MiddleName2: '',
    Lastname1: eventInfo.ownerLastName ?? '',
    Lastname2: eventInfo.fianceeLastName ?? '',

    // Date
    Weekday: date?.toLocaleDateString('en-US', { weekday: 'long' }) ?? '',
    Day: ORDINALS[date?.getDate() ?? 0] ?? '',
    Dth: NUMERIC_ORDINALS[date?.getDate() ?? 0] ?? '',
    Month: date?.toLocaleDateString('en-US', { month: 'long' }) ?? '',
    Mon: date?.toLocaleDateString('en-US', { month: 'short' }) ?? '',
    TwoThousandYear: date ? `Two Thousand ${getTwoDigitsOrdinalFromYear(date.getFullYear())}` : '',
    Oclock: date ? getOclock(date) : '',
    Timeofday: date ? getTimeOfDay(date) : '',
    Am: date ? getAmOrPm(date) : '',

    // Venue
    Venue: venueInfo.name ?? '',
    Address: venueInfo.address ? venueInfo.address.split(',')[0] : '',

    // Location
    City: eventInfo.locationInfo?.city ?? '',
    St: US_STATE_NAME_TO_ABBREVIATION[eventInfo.locationInfo?.state?.toUpperCase() ?? ''] ?? '',
    State: eventInfo.locationInfo?.state ?? ''
  };

  const YYYY = date?.toLocaleDateString('en-US', { year: 'numeric' }) ?? '';

  const fields: Record<string, string> = {
    // Alphabetics require case variants
    ...mapRecord(Alphabetic, str => toTitleCase(str)),
    ...mapRecord(Alphabetic, str => str.toUpperCase()),
    ...mapRecord(Alphabetic, str => str.toLowerCase()),

    // Numeric
    D: date?.toLocaleDateString('en-US', { day: 'numeric' }) ?? '',
    DD: date?.toLocaleDateString('en-US', { day: '2-digit' }) ?? '',
    M: date?.toLocaleDateString('en-US', { month: 'numeric' }) ?? '',
    MM: date?.toLocaleDateString('en-US', { month: '2-digit' }) ?? '',
    YY: YYYY.slice(-2),
    YYYY,
    h: date?.toLocaleTimeString('en-US', { hour: 'numeric', hour12: true }).replace(/ (AM|PM)/, '') ?? '',
    m: date?.getMinutes()?.toString() ?? '',
    mm: date?.getMinutes()?.toString().padStart(2, '0') ?? ''
  };

  const layers = page.layers.filter(layer => layer.type === 'text') as TextLayerData[];
  const filledPatterns: string[] = [];
  for (const layer of layers) {
    const pattern = layer.textData.pattern ?? THEME_SYSTEM_LAYER_NAME_TO_PATTERN[layer.name ?? ''];
    if (!pattern) continue;
    const filledPattern = fillPattern(pattern, fields);
    layer.textData.content = filledPattern;
    filledPatterns.push(filledPattern);
  }

  return filledPatterns;
}
